import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import { getStepErrors, StepErrorType } from '@/types/stepErrorTypes.ts';
import StyledInputErrorText from '@/components/StyledInputFields/StyledInputErrorText.tsx';
import { makeErrorMessageString } from '../ErrorMessages.tsx';
import { DataField, ExtractionFieldType } from '@/types/automationFields.ts';
import { ExtractDataStep } from '@/types/automationSteps.ts';
import { useMemo } from 'react';
import SortableFieldsTable, {
  ColumnDef,
} from '@/pages/Editor/components/StepSidebar/StepForms/ExtractDataStepForm/ExtractFieldsModal/SortableTable/SortableFieldsTable.tsx';
import { dataFieldTableColumns } from '@/pages/Editor/components/StepSidebar/StepForms/ExtractDataStepForm/ExtractFieldsModal/DataFields/DataFieldTableColumns.tsx';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';

const emptyExtractionField: DataField = {
  field_id: 'DEFAULT',
  name: '',
  field_type: ExtractionFieldType.Text,
  is_required: false,
  default_value: null,
  description: null,
  formatting_rules: null,
  blank_as_zero_allowed: false,
  dependencies: [],
  internet_access: false,
};

interface Props {
  step: ExtractDataStep;
}

export default function ExtractDataFieldsTable({ step }: Props) {
  const { fields, onChange } = useUpdateStepFields<{ fields: DataField[] }>({ step });
  const rows = fields.fields;

  // Errors
  const fieldInvalidIdErrors = getStepErrors(step, StepErrorType.ExtractFieldInvalidId);
  const fieldInvalidId = useMemo(
    () => makeErrorMessageString(fieldInvalidIdErrors, step.fields),
    [fieldInvalidIdErrors, step.fields],
  );

  const fieldEmptyNameErrors = getStepErrors(step, StepErrorType.ExtractFieldEmptyName);
  const fieldEmptyName = useMemo(
    () => makeErrorMessageString(fieldEmptyNameErrors, step.fields),
    [fieldEmptyNameErrors, step.fields],
  );

  const fieldInvalidDependencyErrors = getStepErrors(
    step,
    StepErrorType.ExtractFieldInvalidDependency,
  );
  const fieldInvalidDependency = useMemo(
    () => makeErrorMessageString(fieldInvalidDependencyErrors, step.fields),
    [fieldInvalidDependencyErrors, step.fields],
  );

  const fieldDuplicateId = useStepSpecificError(step, StepErrorType.ExtractFieldDuplicateId);
  const fieldDuplicateName = useStepSpecificError(step, StepErrorType.ExtractFieldDuplicateName);
  const fieldCircularDependency = useStepSpecificError(
    step,
    StepErrorType.ExtractFieldCircularDependency,
  );

  const columns: ColumnDef<DataField>[] = dataFieldTableColumns;

  return (
    <SortableFieldsTable<DataField>
      rows={rows}
      getId={(r) => r.field_id}
      onChange={(next) => onChange({ fields: next })}
      columns={columns}
      makeEmptyRow={() => ({ ...emptyExtractionField, field_id: crypto.randomUUID().slice(0, 8) })}
      helperText={
        <>
          Use <code>Extract Fields</code> to identify and pull out specific, structured data (e.g.,
          numbers, names, dates) from within a document or dataset. <strong>No default</strong> preserves the
          platform&apos;s type-empty behavior and records an unknown answer. A <strong>Default</strong> is used only
          when the source has no accepted answer and records a defaulted answer.
        </>
      }
      addRowLabel="Add New Field"
      errors={[
        <StyledInputErrorText key="dup-id">{fieldDuplicateId}</StyledInputErrorText>,
        <StyledInputErrorText key="dup">{fieldDuplicateName}</StyledInputErrorText>,
        <StyledInputErrorText key="id">{fieldInvalidId}</StyledInputErrorText>,
        <StyledInputErrorText key="empty">{fieldEmptyName}</StyledInputErrorText>,
        <StyledInputErrorText key="dep">{fieldInvalidDependency}</StyledInputErrorText>,
        <StyledInputErrorText key="circ">{fieldCircularDependency}</StyledInputErrorText>,
      ]}
      confirm={{
        title: 'Delete Extract Field',
        message: 'Are you sure you want to delete this field?',
        confirmText: 'Yes, delete',
      }}
    />
  );
}
